import {
  ActionCommand,
  CalendarEvents,
  CustomEvent,
  HolidayEvent,
  Id,
  ScheduleQueryParams,
  Shift,
  ShowEventsOptions,
  Task,
  Uri,
  convertCustomEventsToCalendarEvents,
  convertHolidayEventsToCalendarEvents,
  convertTasksToCalendarEvents,
  convertTimeOffEventsToCalendarEvents,
  currentStoreWeekStartAndEndDates,
  mergeEvents,
  toDateString,
} from '@nextforce/api'
import { useIsFocused } from '@react-navigation/native'
import { useTheme } from '@rneui/themed'
import { onlineManager } from '@tanstack/react-query'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { useCustomEvents } from 'custom_events/queries/list'
import { HomeStackParamList, ScheduleScreenProps } from 'home/stack/types'
import moment from 'moment'
import { usePerformOpenShiftsAction } from 'open_shifts/queries/perform_action'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { useTimeOffRequestList } from 'request_time_off/queries/list'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderButton } from 'shared/header/header_button'
import { showHeaderTitle } from 'shared/header/header_title'
import { generateUniqueId } from 'shared/utils'
import { DEFAULT_STORE_PREFERENCES } from 'stores/constants'
import { useRecentStoresContext } from 'stores/context'
import { showStoreHeaderDropdown } from 'stores/dropdown'
import { useStores } from 'stores/queries/list'
import { storeHasManagementRole } from 'stores/utils'
import { useTasks } from 'tasks/queries/list'
import { useUser } from 'users/queries/detail'
import { useFiltersContext } from './filters/context'
import { useHolidayEventList } from './holidays/queries/list'
import { useSchedule } from './queries/list'
import { useScheduleContext } from './schedule_context'
import { showScheduleHeaderDropdown } from './schedule_dropdown'
import {
  filterEvents,
  getDefaultSchedule,
  getScheduleTitle,
  getSimilarSchedule,
} from './schedules'
import ScheduleView from './views/schedule'

const DEFAULT_SHOW_EVENT_OPTIONS: ShowEventsOptions = {
  holidays: true,
  timeOff: true,
  customEvents: true,
  tasks: true,
}

export function ScheduleScreen({ navigation, route }: ScheduleScreenProps) {
  const { theme } = useTheme()
  const { handleNavigation } = useRoutingContext()
  const isFocused = useIsFocused()

  const { data: user } = useUser()
  const { data: stores } = useStores(data => data.stores)
  const { activeStoreId, setActiveStoreId, recentStores } =
    useRecentStoresContext()
  const {
    schedule,
    setSchedule,
    availableSchedules,
    showCustomFilters,
    enableCustomEvents,
    showSummaryTiles,
    setShowSummaryTiles,
  } = useScheduleContext()

  const [showWeeklySummary, setShowWeeklySummary] = useState<boolean>(false)

  const [showEvents, setShowEvents] = useState<ShowEventsOptions>(
    DEFAULT_SHOW_EVENT_OPTIONS
  )

  const showOpenShiftsFilters =
    schedule?.calendar === 'helper' && schedule?.openShiftsOnly

  const { filters, activeFilterNames } = useFiltersContext()

  useEffect(() => {
    if (
      activeStoreId &&
      setActiveStoreId &&
      schedule?.calendar === 'store' &&
      schedule?.storeId !== undefined &&
      schedule?.storeId !== activeStoreId &&
      isFocused
    ) {
      setActiveStoreId(schedule.storeId)
    }
  }, [activeStoreId, isFocused, schedule, setActiveStoreId])

  useEffect(() => {
    if (route.name === 'open_shifts') {
      setSchedule({ calendar: 'helper', openShiftsOnly: true })
    }
  }, [route.name, setSchedule])

  const showSummaryTilesOption = schedule?.calendar !== 'helper'

  const showEventsTilesOption = schedule?.calendar !== 'helper'

  const activeStore = stores?.find(s => s.id === activeStoreId)

  const showWeeklySummaryOption = storeHasManagementRole(activeStore)

  const isOpenShiftsSchedule =
    schedule?.calendar === 'helper' && schedule?.openShiftsOnly

  const showStoreDropdown =
    user?.permissions?.showStoreDropdown && !isOpenShiftsSchedule

  const [localDate, setLocalDate] = useState<string>(
    route.params?.date ?? toDateString(new Date())
  )

  const { startDate, endDate } = useMemo(
    () =>
      currentStoreWeekStartAndEndDates(
        localDate,
        activeStore?.weekStartDay ?? 0
      ),
    [activeStore?.weekStartDay, localDate]
  )

  const [pageUri, setPageUri] = useState<Uri>()

  useEffect(() => {
    if (
      route.params?.date &&
      route.params?.date !== localDate &&
      localDate === undefined
    ) {
      setLocalDate(route.params?.date)
    }
  }, [localDate, route.params?.date])

  const scheduleQueryInput: ScheduleQueryParams | undefined = useMemo(
    () => schedule && { schedule, startDate, endDate, pageUri },
    [schedule, startDate, endDate, pageUri]
  )

  const scheduleQuery = useSchedule(
    scheduleQueryInput,
    generateUniqueId,
    !!schedule
  )

  const openShiftsScheduleQuery = useSchedule(
    {
      schedule: { calendar: 'helper', openShiftsOnly: true },
      startDate,
      endDate,
      pageUri,
    },
    generateUniqueId,
    schedule?.calendar === 'helper'
  )

  const shiftsCalendarEvents = useMemo(
    () =>
      showOpenShiftsFilters
        ? openShiftsScheduleQuery.data?.events
        : scheduleQuery.data?.events,
    [openShiftsScheduleQuery.data, scheduleQuery.data, showOpenShiftsFilters]
  )

  const timeOffRequestsQuery = useTimeOffRequestList(
    { startDate: startDate, endDate: endDate },
    schedule
  )

  const timeOffRequestCalendarEvents = convertTimeOffEventsToCalendarEvents(
    timeOffRequestsQuery.data,
    schedule?.calendar,
    startDate,
    generateUniqueId
  )

  const holidayEventsQuery = useHolidayEventList({
    fromDate: startDate,
    toDate: endDate,
    storeId: activeStoreId,
  })

  const tasksQuery = useTasks(
    {
      storeId: activeStoreId,
      fromDate: startDate,
      toDate: endDate,
      userId: user?.id,
    },
    schedule?.calendar
  )

  const customEventsQuery = useCustomEvents({
    fromDate: startDate,
    toDate: endDate,
    storeId: activeStoreId,
    userId: user?.id,
  })

  const holidayCalendarEvents = convertHolidayEventsToCalendarEvents(
    holidayEventsQuery.data,
    startDate,
    generateUniqueId
  )

  const customCalendarEvents = useMemo(() => {
    return convertCustomEventsToCalendarEvents(
      customEventsQuery.data,
      startDate,
      generateUniqueId
    )
  }, [customEventsQuery.data, startDate])

  const taskCalendarEvents = useMemo(() => {
    return convertTasksToCalendarEvents(
      tasksQuery.data,
      startDate,
      schedule?.calendar,
      generateUniqueId
    )
  }, [tasksQuery.data, startDate, schedule?.calendar])

  const nonShiftCalendarEventsList = useMemo(
    () => [
      holidayCalendarEvents,
      customCalendarEvents,
      taskCalendarEvents,
      timeOffRequestCalendarEvents,
    ],
    [
      customCalendarEvents,
      holidayCalendarEvents,
      taskCalendarEvents,
      timeOffRequestCalendarEvents,
    ]
  )

  const calendarEvents: CalendarEvents | undefined = useMemo(
    () =>
      mergeEvents(shiftsCalendarEvents, nonShiftCalendarEventsList, startDate),
    [nonShiftCalendarEventsList, shiftsCalendarEvents, startDate]
  )

  const onCalendarDateSelected = useCallback((date: Date) => {
    const dateString = toDateString(date)
    setLocalDate(dateString)
    setPageUri(undefined)
  }, [])

  const onNextWeek = useCallback(() => {
    const today = toDateString(moment())
    const nextWeekDate = toDateString(moment(localDate).add(7, 'days'))
    const { startDate: nextWeekStart, endDate: nextWeekEnd } =
      currentStoreWeekStartAndEndDates(
        nextWeekDate,
        activeStore?.weekStartDay ?? 0
      )

    const newDate = moment(today).isBetween(
      nextWeekStart,
      nextWeekEnd,
      'day',
      '[]'
    )
      ? today
      : nextWeekDate

    if (newDate !== localDate) {
      setLocalDate(newDate)
      setPageUri(scheduleQuery.data?.nextPageUri)
    }
  }, [scheduleQuery.data?.nextPageUri, localDate, activeStore?.weekStartDay])

  const onPreviousWeek = useCallback(() => {
    const today = toDateString(moment())
    const prevWeekDate = toDateString(moment(localDate).subtract(7, 'days'))
    const { startDate: prevWeekStart, endDate: prevWeekEnd } =
      currentStoreWeekStartAndEndDates(
        prevWeekDate,
        activeStore?.weekStartDay ?? 0
      )

    const newDate = moment(today).isBetween(
      prevWeekStart,
      prevWeekEnd,
      'day',
      '[]'
    )
      ? today
      : prevWeekDate

    if (newDate !== localDate) {
      setLocalDate(newDate)
      setPageUri(scheduleQuery.data?.previousPageUri)
    }
  }, [
    localDate,
    scheduleQuery.data?.previousPageUri,
    activeStore?.weekStartDay,
  ])

  const onGotoNextShifts = useCallback(
    (date?: string) => {
      setPageUri(undefined)
      setSchedule({ calendar: 'helper', openShiftsOnly: true })
      if (date) {
        setLocalDate(date)
      }
    },
    [setSchedule]
  )

  const onCommandButtonPress = useCallback(
    async (command: ActionCommand) => {
      if (command.name === 'shift_rating') {
        const rateStore = command.action.params?.rate_store
        navigation.setParams({
          uri: command.action.uri,
          rateStore: rateStore,
        })
      }
    },
    [navigation]
  )

  const onCloseRatingMenu = useCallback(() => {
    navigation.setParams({
      uri: undefined,
      rateCrew: undefined,
      rateStore: undefined,
      rateShifts: undefined,
    })
  }, [navigation])

  useEffect(() => {
    if (route.params?.date) {
      setLocalDate(route.params.date)
    }
  }, [route.params?.date])

  const filteredEvents = useMemo(() => {
    return filterEvents(calendarEvents, schedule, filters, showEvents)
  }, [calendarEvents, filters, schedule, showEvents])

  const onGotoShiftDetails = useCallback(
    (shift: Shift) => {
      if (!shift.route) {
        return
      }

      handleNavigation({
        routeName: shift.route.name as keyof HomeStackParamList,
        routeParams: shift.route.params && { ...shift.route.params },
      })
    },
    [handleNavigation]
  )

  const onAddShift = useCallback(
    (isAddAndAssign: boolean) => {
      handleNavigation({
        routeName: 'edit_shift',
        routeParams: {
          addAndAssign: isAddAndAssign,
        },
      })
    },
    [handleNavigation]
  )

  const onRequestTimeOff = useCallback(() => {
    handleNavigation({ routeName: 'request_time_off', routeParams: {} })
  }, [handleNavigation])

  const onRequestNonAvailability = useCallback(() => {
    handleNavigation({ routeName: 'edit_non_availability', routeParams: {} })
  }, [handleNavigation])

  const onAddCustomEvent = useCallback(() => {
    handleNavigation({ routeName: 'edit_custom_event', routeParams: {} })
  }, [handleNavigation])

  const onAddTask = useCallback(() => {
    handleNavigation({ routeName: 'edit_task', routeParams: {} })
  }, [handleNavigation])

  const onEditCustomEvent = useCallback(
    (customEvent: CustomEvent) => {
      handleNavigation({
        routeName: 'edit_custom_event',
        routeParams: { customEventId: customEvent.id },
      })
    },
    [handleNavigation]
  )

  const onPressTask = useCallback(
    (task: Task) => {
      if (!task.occurrences || task.occurrences.length === 0) {
        return
      }
      const taskOccurrence = task.occurrences[0]
      handleNavigation({
        routeName: 'task_details',
        routeParams: {
          taskId: taskOccurrence.taskId,
          fromDate: taskOccurrence.dueDate,
          toDate: task.occurrences[task.occurrences.length - 1].dueDate,
        },
      })
    },
    [handleNavigation]
  )

  const onEditHolidayEvent = useCallback(
    (holidayEvent: HolidayEvent) => {
      handleNavigation({
        routeName: 'edit_holiday_event',
        routeParams: {
          holidayEventId: holidayEvent.id,
        },
      })
    },
    [handleNavigation]
  )

  const showNoShiftsCard = useCallback(
    (openShiftsCount?: number) => {
      if (schedule?.calendar === 'helper' && !schedule?.openShiftsOnly) {
        if (openShiftsCount && openShiftsCount > 0) {
          return true
        }
      }

      return false
    },
    [schedule?.calendar, schedule?.openShiftsOnly]
  )

  const onPressTimeOffSummary = (storeId: string) => {
    if (user?.permissions?.canApproveTimeOffRequests) {
      handleNavigation({
        routeName: 'time_off_request_list',
        routeParams: { storeId: storeId },
      })
    }
  }

  const openShiftsMutation = usePerformOpenShiftsAction()

  const performOpenShiftAction = useCallback(
    async (command: ActionCommand) => {
      try {
        const shift = await openShiftsMutation.mutateAsync(command.action)
        if (shift) {
          return shift
        }
      } catch (error) {
        console.error(error)
      }

      return
    },
    [openShiftsMutation]
  )

  useLayoutEffect(() => {
    if (schedule?.openShiftsOnly === true) {
      if (showOpenShiftsFilters && schedule && onlineManager.isOnline()) {
        showHeaderButton({
          navigation,
          theme,
          title: t('filter'),
          onPress: () => handleNavigation({ routeName: 'filter_chooser' }),
          position: 'right',
        })
      }
    } else {
      navigation.setOptions({ headerRight: undefined })
    }
  }, [handleNavigation, navigation, schedule, showOpenShiftsFilters, theme])

  useLayoutEffect(() => {
    showHeaderTitle(
      navigation,
      schedule
        ? getScheduleTitle(schedule, !showStoreDropdown, stores ?? [])
        : t('schedule')
    )

    if (showStoreDropdown) {
      showStoreHeaderDropdown(
        navigation,
        handleNavigation,
        { stores, activeStoreId, recentStores, setActiveStoreId },
        (storeId: Id) => {
          const similarSchedule =
            schedule &&
            getSimilarSchedule(availableSchedules, schedule, storeId)
          {
            setPageUri(undefined)
            setSchedule(
              similarSchedule ??
                getDefaultSchedule(
                  user?.role ?? 'helper',
                  availableSchedules,
                  storeId
                )
            )
            setLocalDate(toDateString(new Date()))
          }
        }
      )
    }
    showScheduleHeaderDropdown(
      availableSchedules,
      schedule,
      navigation,
      handleNavigation,
      selectedSchedule => {
        setPageUri(undefined)
        setSchedule(selectedSchedule)
        setLocalDate(toDateString(new Date()))
      },

      activeFilterNames,
      stores ?? [],
      {
        showWeeklySummary,
        setShowWeeklySummary,
        showWeeklySummaryOption,
      },
      { showSummaryTiles, setShowSummaryTiles, showSummaryTilesOption },
      { showEvents, setShowEvents, showEventsTilesOption },
      showStoreDropdown ? activeStoreId : undefined,
      showCustomFilters
    )
  }, [
    activeStoreId,
    availableSchedules,
    navigation,
    schedule,
    activeFilterNames,
    showStoreDropdown,
    stores,
    user?.role,
    showCustomFilters,
    showSummaryTiles,
    setShowSummaryTiles,
    showSummaryTilesOption,
    showWeeklySummary,
    showWeeklySummaryOption,
    theme,
    showEvents,
    showEventsTilesOption,
    setSchedule,
    handleNavigation,
    recentStores,
    setActiveStoreId,
  ])

  if (!user?.role) {
    return null
  }

  return (
    <StatusBoundary
      isLoading={
        !user ||
        scheduleQuery.isLoading ||
        openShiftsScheduleQuery.isLoading
      }
      error={scheduleQuery.error || openShiftsScheduleQuery.error}
    >
      <ScheduleView
        role={user.role}
        events={filteredEvents}
        showMarkedDates={schedule?.calendar === 'helper'}
        hasNextWeek={!!scheduleQuery.data?.nextPageUri}
        hasPreviousWeek={!!scheduleQuery.data?.previousPageUri}
        storePreferences={activeStore?.preferences ?? DEFAULT_STORE_PREFERENCES}
        fetchingNextWeek={
          scheduleQuery.isPlaceholderData &&
          pageUri === scheduleQuery.data?.nextPageUri
        }
        fetchingPreviousWeek={
          scheduleQuery.isPlaceholderData &&
          pageUri === scheduleQuery.data?.previousPageUri
        }
        onCalendarDateSelected={onCalendarDateSelected}
        currentDate={localDate}
        onNextWeek={onNextWeek}
        onPreviousWeek={onPreviousWeek}
        onGotoShiftDetails={onGotoShiftDetails}
        onGotoNextShifts={onGotoNextShifts}
        onRequestTimeOff={
          activeStore?.preferences?.showTimeOffRequest &&
          user?.role !== 'observer' &&
          user.role !== 'admin' &&
          !user?.isAtc
            ? onRequestTimeOff
            : undefined
        }
        onRequestNonAvailability={
          activeStore?.preferences?.showNonAvailability &&
          user?.role !== 'observer' &&
          user?.role !== 'admin' &&
          !user?.isAtc
            ? onRequestNonAvailability
            : undefined
        }
        onPressTimeOffSummary={onPressTimeOffSummary}
        onHelperPressPendingTimeOff={timeOffEvent =>
          handleNavigation({
            routeName: 'request_time_off',
            routeParams: {
              timeOffEventId: timeOffEvent.id,
            },
          })
        }
        onAddShift={onAddShift}
        onCloseRatingMenu={onCloseRatingMenu}
        storeId={activeStoreId}
        isMyShiftsSchedule={
          schedule?.calendar === 'helper' && !schedule?.openShiftsOnly
        }
        performOpenShiftAction={performOpenShiftAction}
        isOpenShiftsSchedule={isOpenShiftsSchedule}
        showSummaryTiles={showSummaryTiles ?? true}
        showNoShiftsCard={showNoShiftsCard}
        showWeeklySummary={showWeeklySummary}
        showWeeklySummaryOption={showWeeklySummaryOption}
        setShowWeeklySummary={setShowWeeklySummary}
        showCrewRatingMenu={route.params?.rateCrew}
        showStoreRatingMenu={route.params?.rateStore}
        showMultipleShiftsRatingMenu={route.params?.rateShifts}
        uri={route.params?.uri}
        onCommandButtonPress={onCommandButtonPress}
        openShiftsScheduleEvents={openShiftsScheduleQuery.data?.events}
        showCustomEvents={
          enableCustomEvents && user?.permissions?.canEditCustomEvents
        }
        onAddCustomEvent={onAddCustomEvent}
        onEditCustomEvent={onEditCustomEvent}
        onEditHolidayEvent={onEditHolidayEvent}
        shiftId={route.params?.shiftId}
        onAddTask={onAddTask}
        onPressTask={onPressTask}
      />
    </StatusBoundary>
  )
}
